import React, { useCallback, useMemo } from "react";
import { GridLayout, VideoGalleryProps, VideoGalleryRemoteParticipant, VideoStreamOptions } from "@azure/communication-react";
import { Spinner, SpinnerSize, Stack } from "@fluentui/react";
import { useCallAdapter } from "live-lesson/shared/feature-call-ui-react";
import { videoGalleryContainerStyles } from "../../styles/ModeratorVideoGalleryStyles";
import { StudentVideoTile } from "./StudentVideoTile";
import { CreateMenuItemsCallbacks } from "../util/createMenuItems";


export interface ModeratorVideoGalleryProps {

    videoGalleryProps: VideoGalleryProps;

    hasMultiplePages: boolean;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    galleryPage: number;
    galleryNumPages: number;
    participantsToShow: VideoGalleryRemoteParticipant[];
    goToPrevPage: () => void;
    goToNextPage: () => void;

    menuItemCallbacks: CreateMenuItemsCallbacks;

    localPreviewTile?: JSX.Element;
}

export const ModeratorVideoGallery = (props: ModeratorVideoGalleryProps): JSX.Element => {

    const {
        videoGalleryProps,
        hasMultiplePages,
        hasPrevPage,
        hasNextPage,
        galleryPage,
        galleryNumPages,
        participantsToShow,
        goToPrevPage,
        goToNextPage,

        menuItemCallbacks,

        localPreviewTile,
    } = props;

    const callAdapter = useCallAdapter();

    const createRemoteStreamView = useCallback(
        (remoteUserId?: string, options?: VideoStreamOptions) => callAdapter.createStreamView(remoteUserId, options)
    , [callAdapter]);
    const disposeRemoteVideoStreamView = useCallback(
        (userId: string) => callAdapter.disposeRemoteVideoStreamView(userId)
    , [callAdapter]);

    const studentTiles = participantsToShow.map((remoteParticipant: VideoGalleryRemoteParticipant) => (
        <StudentVideoTile
            key={remoteParticipant.userId}
            remoteParticipant={remoteParticipant}
            menuItemCallbacks={menuItemCallbacks}
            createRemoteStreamView={createRemoteStreamView}
            disposeRemoteVideoStreamView={disposeRemoteVideoStreamView}
        />
    ));

    // Gallery tiles = localPreview + Students.
    // localPreview is only shown in the gallery when activeElement is not shown.
    //
    // If its shown or not is determined by the parent component
    // => localPreviewTile is undefined when it should not be shown.
    const videoGalleryTiles: JSX.Element[] = useMemo(() => {
        if (localPreviewTile) {
            return [localPreviewTile, ...studentTiles];
        }
        return studentTiles.length > 0 ? studentTiles : [(
            // see https://react.fluentui.dev/?path=/docs/components-spinner--docs
            <Spinner
                labelPosition="bottom"
                label="Warte auf weitere Teilnehmer..."
                size={SpinnerSize.large} />
        )];
    }, [studentTiles, localPreviewTile]);

    return (
        <Stack.Item styles={videoGalleryContainerStyles}>
            {videoGalleryProps &&
                <>
                    <GridLayout>
                        {...videoGalleryTiles}
                    </GridLayout>
                </>
            }
        </Stack.Item>
    );
};
